import { MetricUnit } from '@/__generated__/graphql';
import { useFormatters } from '@/hooks/useFormatters';
import { AU_SUCCESS, AU_TINT, AU_TEXT_DIM } from '@/theme/au-compat';
import { BatteryLevelIcon } from '@components/BatteryLevelIcon';
import { MovementDataRenderer } from '@features/gateCommon';
import { BATTERY_LOW_THRESHOLD } from '@features/gateCommon/utils/battery';
import { Trans } from '@lingui/macro';
import { BatteryUnknown } from '@mui/icons-material';
import { Box, Skeleton } from '@mui/material';
import { ComponentProps, FC } from 'react';
import { ConnectionStateRenderer } from './dataComponents/ConnectionStateRenderer';

interface Props {
  movementId: number | null;
}

/**
 * `.oc-conn` — la tira de conexión y batería del encabezado del modal.
 *
 * **Va dentro del título, no en un panel propio.** Era el área `status` de la grilla, o sea una
 * card al lado del nombre de la compuerta; en el espécimen es parte de la cabecera del diálogo, a
 * la derecha del título (`modal-frame.tsx § ConnectionStatus`). La diferencia no es de gusto: un
 * panel es una unidad de contenido y esto es el encabezado de la ventana, así que darle card lo
 * ponía al mismo nivel que el resumen o el histórico.
 *
 * Los dos `*Renderer` traen su propio `Suspense` y su propio error boundary, así que esto puede
 * vivir en el `DialogTitle`, que queda **fuera** del `Suspense` que `GlobalModal` monta para sus
 * children.
 */
export const ConnectionStatus: FC<Props> = ({ movementId }) => {
  const { formatMetricValue } = useFormatters();

  if (!movementId) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
      <ConnectionStateRenderer
        movementId={movementId}
        render={({ lastConnection }) => {
          const connected = lastConnection?.[1] === 1;
          return (
            <Item>
              {/* El halo del punto conectado es `--au-success-border` (el 32% del tono), no su
                  relleno: es un anillo, y el `-bg` al 12% no se ve contra la cabecera. */}
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: connected ? AU_SUCCESS : AU_TEXT_DIM,
                  boxShadow: connected ? `0 0 0 3px ${AU_TINT.success.border}` : 'none',
                }}
              />
              {connected ? <Trans>conectado</Trans> : <Trans>desconectado</Trans>}
            </Item>
          );
        }}
        fallback={() => <Skeleton width={90} />}
      />

      <MovementDataRenderer
        movementId={movementId}
        render={({ gate }) => {
          const batteryLevel = gate.movements2.lastBattery?.[1] ?? null;
          // `!== null` y no falsy: una batería en 0 es un dato, y de los graves (AMARU-876).
          const low = batteryLevel !== null && batteryLevel < BATTERY_LOW_THRESHOLD;
          return (
            /* El color de severidad va **sólo en el glifo**, no en el grupo.
               `.oc-conn-battery[data-low]` tiñe el icono y deja el porcentaje en `--au-text`: el
               número es el dato exacto y no cambia de significado por estar bajo; lo que avisa es
               la marca. Teñir los dos bajaba la cifra a `#d79895` sobre la cabecera, que además es
               menos contraste para lo único que hay que leer.

               `--au-error-text` y no la base pelada: el icono mide 16 al lado de texto de 11,5 y se
               lee como parte de la tira, no como una marca de gráfico. */
            <Item>
              {batteryLevel !== null ? (
                <BatteryLevelIcon
                  battery={batteryLevel}
                  sx={{ fontSize: 16, color: low ? AU_TINT.error.text : undefined }}
                />
              ) : (
                <BatteryUnknown sx={{ fontSize: 16 }} />
              )}
              {batteryLevel !== null
                ? formatMetricValue(batteryLevel, { unit: MetricUnit.Percent, precision: 0 })
                : 'N/D'}
            </Item>
          );
        }}
        fallback={() => <Skeleton width={56} />}
      />
    </Box>
  );
};

/** `.oc-conn-item` — 11,5px/600, con 6px entre la marca y su palabra. */
const Item: FC<ComponentProps<typeof Box>> = ({ sx, ...props }) => (
  <Box
    {...props}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: 11.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      ...sx,
    }}
  />
);
